import { CopyIcon, NewTabIcon, PrintIcon } from '@farol-ds/react'
import {
  forwardRef,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import styles from './context-menu.module.scss'
import ContextMenuContent from './context-menu-content'
import { ContextMenuItem } from './context-menu-item'

type MetaProps = Record<string, unknown>

/**
 *
 * @export
 * @interface OnHandleContextMenuCtx
 * @property {RefObject<object>} metaProps - The metaProps ref object that is passed to the onMetricsCallback function.
 * @description Represents the context object that is passed to the onHandleContextMenu function.
 */
export interface OnHandleContextMenuCtx {
  metaProps: MetaProps
}

/**
 *
 * @export
 * @interface ContextMenuProps
 * @property {string} scope - The selector for the section or element where the custom ContextMenu should be displayed. (optional)
 * @property {(eventName: string, eventProps: object, metaProps: object) => void} onMetricsCallback - Receives a custom callback set with rules for sending metrics.
 * @description Represents the properties of the ContextMenu component.
 */
export interface ContextMenuProps {
  scope?: string
  onMetricsCallback?: (eventName: string, eventProps: object, metaProps: MetaProps) => void
  onHandleContextMenu?: (event: MouseEvent, ctx: OnHandleContextMenuCtx) => void
  onCopySuccess?: (message: string, options?: { id?: string }) => void
  dialogRef?: RefObject<HTMLDivElement>
  extraContextMenuItems?: ReactElement<typeof ContextMenuItem>[]
}

/**
 * @export
 * @param {ContextMenuProps} props - The optional properties for the ContextMenu component: scope and onMetricsCallback.
 * @return {JSX.Element | null} - The rendered ContextMenu component.
 * @description The ContextMenu component customizes the browser's right-click menu.
 * It provides standard items such as "Imprimir", "Copiar", and "Abrir link em nova guia".
 * You can set the scope in which the ContextMenu should be displayed on the page,
 * and you can also add new custom items with your own rules.
 */

const BORDER_GAP = 200

export const ContextMenu = forwardRef<HTMLDivElement, ContextMenuProps>(function ContextMenu(
  props,
  forwardedRef
): JSX.Element | null {
  const {
    scope,
    dialogRef,
    extraContextMenuItems,
    onHandleContextMenu,
    onMetricsCallback,
    onCopySuccess,
  } = props

  const hasRenderedRef = useRef(false)
  const metaProps = useRef<MetaProps>({})
  const [enableLinkOption, setEnableLinkOption] = useState(false)
  const [enableCopyOption, setEnableCopyOption] = useState(false)
  const [enablePrintOption, setEnablePrintOption] = useState(false)
  const [clicked, setClicked] = useState(false)
  const [linkNewTab, setLinkNewTab] = useState<HTMLElement>()
  const [points, setPoints] = useState({
    x: 0,
    y: 0,
  })
  const generateRandomNumbers = () => Math.floor(Math.random() * 100000)
  const wrapperId = `ContextMenu-${generateRandomNumbers()}`

  const findFirstParentAnchor = useCallback(
    (element: HTMLElement | null): HTMLElement | void | null => {
      if (!element) return null

      if (element.tagName === 'A' && element.hasAttribute('href')) {
        setLinkNewTab(element)
        return element
      }

      return findFirstParentAnchor(element.parentElement)
    },
    [setLinkNewTab]
  )

  const handleClick = useCallback(() => setClicked(false), [setClicked])

  const getDialogPoints = useCallback(() => {
    const dialogPoints = {
      x: 0,
      y: 0,
    }
    return dialogRef?.current ? dialogRef.current.getBoundingClientRect() : dialogPoints
  }, [dialogRef])

  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      const dialogPoints = getDialogPoints()
      event.preventDefault()
      setClicked(true)
      setEnablePrintOption(true)
      setEnableCopyOption(!!window.getSelection()?.toString())
      setEnableLinkOption(!!findFirstParentAnchor(event.target as HTMLElement))
      setPoints({
        x: event.pageX - dialogPoints.x,
        y: event.pageY - dialogPoints.y,
      })

      const ctx: OnHandleContextMenuCtx = { metaProps: metaProps.current }
      metaProps.current = onHandleContextMenu?.(event, ctx) ?? metaProps.current
    },
    [
      setClicked,
      setEnablePrintOption,
      setEnableCopyOption,
      setPoints,
      findFirstParentAnchor,
      onHandleContextMenu,
      getDialogPoints,
    ]
  )

  useEffect(() => {
    const scopeElement: HTMLElement | null = document.querySelector(scope || 'body')
    document.addEventListener('click', handleClick)
    scopeElement?.addEventListener('contextmenu', handleContextMenu)
    document.getElementById(wrapperId)?.addEventListener('contextmenu', handleContextMenu)
    return () => {
      document.removeEventListener('click', handleClick)
      scopeElement?.removeEventListener('contextmenu', handleContextMenu)
    }
  }, [scope, handleClick, handleContextMenu, wrapperId])

  const onMetrics = useCallback(
    (eventName?: string) => {
      onMetricsCallback?.(
        `CustomContextMenu.${eventName}`,
        { from_component: 'CustomContextMenu' },
        metaProps.current
      )
    },
    [onMetricsCallback]
  )

  useEffect(() => {
    if (clicked && !hasRenderedRef.current) {
      hasRenderedRef.current = true
      onMetrics('Render')
    } else if (!clicked) {
      hasRenderedRef.current = false
    }
  }, [clicked, onMetrics])

  const handleElementAttribute = (attribute: string, value: string, element?: HTMLElement) => {
    value === '' ? element?.removeAttribute(attribute) : element?.setAttribute(attribute, value)
  }

  const onOpenNewTab = (linkNewTab: HTMLElement | undefined) => {
    const originalTarget = linkNewTab?.getAttribute('target') || ''
    const originalRel = linkNewTab?.getAttribute('rel') || ''
    linkNewTab?.setAttribute('target', '_blank')
    linkNewTab?.setAttribute('rel', [originalRel, 'noopener'].filter(Boolean).join(' '))
    linkNewTab?.click()
    handleElementAttribute('target', originalTarget, linkNewTab)
    handleElementAttribute('rel', originalRel, linkNewTab)
    onMetrics('OpenNewTab')
  }

  const onCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    const selection = window.getSelection()
    const textSelected = selection?.toString()
    const anchorNode = selection?.anchorNode
    const dispatcherElement = anchorNode || document
    if (dispatcherElement.dispatchEvent(new Event('copy', { ...e, cancelable: true }))) {
      navigator.clipboard.writeText(textSelected || '')
      onCopySuccess?.('Conteúdo copiado!', { id: 'farol-context-menu-toast' })
    }
    onMetrics('CopyContent')
  }

  const onPrint = () => {
    window.print()
    onMetrics('Print')
  }

  const getDropDownMenuSideClassName = () => {
    if (points.x > window.innerWidth - BORDER_GAP) return 'dropdown-menu-right'

    return 'dropdown-menu-left'
  }

  const getContextMenuItems = () => {
    const contextMenuItems = []

    if (enableLinkOption) {
      contextMenuItems.push(
        <ContextMenuItem
          key="context-menu-link"
          leftIcon={<NewTabIcon />}
          onClick={() => onOpenNewTab(linkNewTab)}
          data-testid="context-menu-link"
          order={2}
        >
          Abrir link em nova guia
        </ContextMenuItem>
      )
    }

    if (enableCopyOption) {
      contextMenuItems.push(
        <ContextMenuItem
          key="context-menu-copy"
          leftIcon={<CopyIcon />}
          onClick={onCopy}
          data-testid="context-menu-copy"
          order={5}
        >
          Copiar
        </ContextMenuItem>
      )
    }

    if (enablePrintOption) {
      contextMenuItems.push(
        <ContextMenuItem
          key="context-menu-print"
          leftIcon={<PrintIcon />}
          onClick={onPrint}
          data-testid="context-menu-print"
          order={8}
        >
          Imprimir
        </ContextMenuItem>
      )
    }

    const fallbackOrder =
      Math.max(
        0,
        ...contextMenuItems
          .map((item) => item.props.order)
          .filter((order) => typeof order === 'number')
      ) + 1

    if (extraContextMenuItems) {
      contextMenuItems.push(...extraContextMenuItems)
    }

    return contextMenuItems.sort((a, b) => {
      const orderA = a.props.order ?? fallbackOrder
      const orderB = b.props.order ?? fallbackOrder
      return orderA - orderB
    })
  }

  return clicked ? (
    <div
      ref={forwardedRef}
      className={styles.root}
      id={wrapperId}
      data-testid="context-menu"
      style={{ position: 'absolute', top: points.y, left: points.x }}
    >
      <ContextMenuContent
        data-testid="contextmenu-content"
        className={styles[getDropDownMenuSideClassName()]}
      >
        {getContextMenuItems()}
      </ContextMenuContent>
    </div>
  ) : null
})
